import { APPLY_FAVORITE_ADDRESS, CITY_PICKER_HINT, FETCH_FAVORITE_ADDRESS } from '../actions';
import {
  applyFavoriteAddressConfirm,
  getFavoriteDeliveryType,
  loadDeliveryStoreIds,
  loadFavoriteData,
  loadFavoriteSheetData,
  mergeFavoriteSheetData,
} from '../common/utils/favorite-address';
import { getCheckoutInfo, setCheckoutInfo } from '../common/utils/checkout-info';
import { isFavoriteAddressEnabled } from '../selectors';
import type { Store } from '../store/types';
import { applyFavoriteAddressCookies, getShortenRegionName } from '../utils';
import { FAVORITE_DELIVERY_TYPE } from '../common/constants';
import { FAVORITE_ADDRESS_HINT_SHOWN } from '../common/constants/local-storage-constants';
import { FavoriteAddressState } from '../types/favorite-address';
import { useRegions } from '../components/header/hooks';
import { trackFavoriteAddressChange } from '../utils/ga-events';

export type FavoriteAddressStore = {
  favoriteAddress: {
    data: FavoriteAddressState;
    loading: boolean;
  };
};

export const INITIAL_FAVORITE_ADDRESS_STATE: FavoriteAddressState = {
  deliveryType: FAVORITE_DELIVERY_TYPE.REGION,
  address: null,
  storeIds: null,
  priorityStoreIds: null,
  shouldResetToRegion: false,
  unavailable: false,
  minPriceText: null,
  sheetLoaded: false,
};

const initialState: FavoriteAddressStore = {
  favoriteAddress: {
    data: INITIAL_FAVORITE_ADDRESS_STATE,
    loading: false,
  },
};

export const favoriteAddressReducer = (store: Store) => {
  store.on('@init', () => initialState);

  store.on(FETCH_FAVORITE_ADDRESS.init, () => {
    const { cookies, orchestratorHost, useCredentials, apiKey, requestID } = store.get();

    if (!isFavoriteAddressEnabled(cookies) || !orchestratorHost) {
      store.dispatch(FETCH_FAVORITE_ADDRESS.reset);

      return;
    }

    store.dispatch(FETCH_FAVORITE_ADDRESS.request);

    return loadFavoriteData({
      cookies,
      checkoutInfo: getCheckoutInfo(),
      apiConfig: { orchestratorHost, useCredentials, apiKey, requestID },
    })
      .then((data) => store.dispatch(FETCH_FAVORITE_ADDRESS.success, data))
      .catch(() => store.dispatch(FETCH_FAVORITE_ADDRESS.failure));
  });

  store.on(FETCH_FAVORITE_ADDRESS.loadSheet, (_, options) => {
    const { cookies, model, orchestratorHost, useCredentials, apiKey, requestID, favoriteAddress } =
      store.get();

    if (!isFavoriteAddressEnabled(cookies) || !orchestratorHost) {
      return;
    }

    if (favoriteAddress.data.sheetLoaded && !options?.force) {
      return;
    }

    return loadFavoriteSheetData({
      checkoutInfo: getCheckoutInfo(),
      regionId: model?.properties.regionId,
      address: favoriteAddress.data.address,
      apiConfig: { orchestratorHost, useCredentials, apiKey, requestID },
      force: options?.force,
    })
      .then((data) => store.dispatch(FETCH_FAVORITE_ADDRESS.sheetSuccess, data))
      .catch(() => {});
  });

  store.on(FETCH_FAVORITE_ADDRESS.sheetSuccess, (state, sheetData) => ({
    favoriteAddress: {
      ...state.favoriteAddress,
      data: mergeFavoriteSheetData(state.favoriteAddress.data, sheetData, getCheckoutInfo()),
    },
  }));

  store.on(APPLY_FAVORITE_ADDRESS.confirm, (state, payload) => {
    const { cookies, orchestratorHost, useCredentials, apiKey, requestID } = store.get();
    const previousType = getFavoriteDeliveryType(state.favoriteAddress.data);
    const typeChanged = payload.deliveryType !== previousType;
    const shouldFetchStoreIds =
      typeChanged &&
      payload.deliveryType === FAVORITE_DELIVERY_TYPE.HOME_DELIVERY &&
      !payload.deletedAddressTypes.includes(FAVORITE_DELIVERY_TYPE.HOME_DELIVERY) &&
      isFavoriteAddressEnabled(cookies) &&
      orchestratorHost;

    const finishConfirm = (storeIds?: string[] | null) => {
      store.dispatch(APPLY_FAVORITE_ADDRESS.confirmed, {
        ...payload,
        ...(storeIds !== undefined ? { storeIds } : {}),
      });
    };

    if (shouldFetchStoreIds) {
      return loadDeliveryStoreIds({
        checkoutInfo: getCheckoutInfo(),
        apiConfig: { orchestratorHost, useCredentials, apiKey, requestID },
      })
        .then(finishConfirm)
        .catch(() => finishConfirm(null));
    }

    finishConfirm();
  });

  store.on(APPLY_FAVORITE_ADDRESS.confirmed, (state, payload) => {
    const { storeIds, ...confirmPayload } = payload;
    const baseData =
      storeIds !== undefined ? { ...state.favoriteAddress.data, storeIds } : state.favoriteAddress.data;
    const { state: data, checkoutInfo: nextCheckoutInfo } = applyFavoriteAddressConfirm(
      baseData,
      confirmPayload,
      getCheckoutInfo(),
    );

    setCheckoutInfo(nextCheckoutInfo);
    applyFavoriteAddressCookies(store.dispatch, store.get().cookies, data);

    return {
      favoriteAddress: {
        data,
        loading: false,
      },
    };
  });

  store.on(FETCH_FAVORITE_ADDRESS.request, (state) => ({
    favoriteAddress: {
      ...state.favoriteAddress,
      loading: true,
    },
  }));

  store.on(FETCH_FAVORITE_ADDRESS.success, (state, data) => {
    const mergedAddress = { ...state.favoriteAddress.data.address, ...data.address };
    const { deliveryTypeChanged } = applyFavoriteAddressCookies(store.dispatch, state.cookies, data);

    if (deliveryTypeChanged && !__IS_SERVER__ && !state.headerDrawers.hintShow) {
      localStorage.removeItem(FAVORITE_ADDRESS_HINT_SHOWN);
      store.dispatch(CITY_PICKER_HINT.openHint);
    }

    if (state.model) {
      const { defaultRegion, currentRegion } = useRegions(state.model);
      const formattedRegionName = getShortenRegionName(
        currentRegion?.regionName || defaultRegion.regionName,
      );

      trackFavoriteAddressChange(state.cookies, data, formattedRegionName);
    }

    return { favoriteAddress: { data: { ...data, address: mergedAddress }, loading: false } };
  });

  store.on(FETCH_FAVORITE_ADDRESS.failure, (state) => ({
    favoriteAddress: {
      ...state.favoriteAddress,
      data: { ...INITIAL_FAVORITE_ADDRESS_STATE, unavailable: true },
      loading: false,
    },
  }));

  store.on(FETCH_FAVORITE_ADDRESS.reset, () => initialState);
};
